import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { GripVertical, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_LANGS = ["en", "hi", "kn", "ta", "ml", "te", "bn", "mr", "gu", "other"];
const FIELD_TYPES = ["text", "tel", "number", "textarea"];
const KNOWN_KEYS_BY_STEP = {
  basic: ["name", "age", "phone", "alt_phone"],
  delivery: ["address", "alt_phone", "city", "district", "state", "pincode", "phone"],
};

export default function AdminForms() {
  const [steps, setSteps] = useState([]);
  const [activeKey, setActiveKey] = useState("basic");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/form_steps");
    setSteps(data);
    const initial = data.find((s) => s.key === activeKey) || data[0];
    if (initial) {
      setActiveKey(initial.key);
      setDraft(JSON.parse(JSON.stringify(initial)));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const switchTo = (key) => {
    const s = steps.find((x) => x.key === key);
    if (s) {
      setActiveKey(key);
      setDraft(JSON.parse(JSON.stringify(s)));
    }
  };

  const updateField = (idx, patch) => {
    setDraft((d) => {
      const next = { ...d, fields: [...d.fields] };
      next.fields[idx] = { ...next.fields[idx], ...patch };
      return next;
    });
  };

  const updateLangValue = (idx, key, lang, val) => {
    setDraft((d) => {
      const next = { ...d, fields: [...d.fields] };
      next.fields[idx] = {
        ...next.fields[idx],
        [key]: { ...(next.fields[idx][key] || {}), [lang]: val },
      };
      return next;
    });
  };

  const moveField = (idx, dir) => {
    setDraft((d) => {
      const arr = [...d.fields];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...d, fields: arr.map((f, i) => ({ ...f, sort: i })) };
    });
  };

  const removeField = (idx) => {
    setDraft((d) => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));
  };

  const addField = () => {
    setDraft((d) => ({
      ...d,
      fields: [
        ...d.fields,
        { key: `custom_${(d.fields?.length || 0) + 1}`, type: "text", label: { en: "New field" }, placeholder: {}, helper: {}, required: false, active: true, sort: d.fields.length },
      ],
    }));
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.patch(`/admin/form_steps/${draft.id}`, {
        key: draft.key,
        title: draft.title || {},
        subtitle: draft.subtitle || {},
        fields: draft.fields,
      });
      toast.success("Form saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return <div className="p-12 text-brand-muted">Loading…</div>;

  const knownKeys = KNOWN_KEYS_BY_STEP[draft.key] || [];

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Funnel forms</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Form Builder</h1>
          <p className="font-sans text-sm text-brand-muted mt-2">
            Customise the question pages your customers see. Add fields, rename labels per language, change order, toggle required.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="forms-save-btn" className="rounded-full bg-brand-primary text-brand-bone h-11 px-6">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* Step selector */}
      <div className="mt-8 inline-flex p-1 bg-white border border-brand-line rounded-full" data-testid="forms-step-tabs">
        {steps.map((s) => (
          <button
            key={s.id}
            onClick={() => switchTo(s.key)}
            data-testid={`forms-tab-${s.key}`}
            className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
              activeKey === s.key ? "bg-brand-dark text-brand-bone" : "text-brand-dark hover:bg-brand-elevated"
            }`}
          >
            {s.key === "basic" ? "Basic details" : "Delivery"}
          </button>
        ))}
      </div>

      {/* Title + subtitle */}
      <div className="mt-8 bg-white border border-brand-line rounded-2xl p-6 space-y-5" data-testid="forms-meta">
        <div>
          <Label className="overline">Page title (per language)</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
            {SUPPORTED_LANGS.map((l) => (
              <Input
                key={l}
                placeholder={l.toUpperCase()}
                value={draft.title?.[l] || ""}
                onChange={(e) => setDraft({ ...draft, title: { ...(draft.title || {}), [l]: e.target.value } })}
                className="h-10 rounded-xl border-brand-line"
              />
            ))}
          </div>
        </div>
        <div>
          <Label className="overline">Page subtitle (per language)</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
            {SUPPORTED_LANGS.map((l) => (
              <Input
                key={l}
                placeholder={l.toUpperCase()}
                value={draft.subtitle?.[l] || ""}
                onChange={(e) => setDraft({ ...draft, subtitle: { ...(draft.subtitle || {}), [l]: e.target.value } })}
                className="h-10 rounded-xl border-brand-line"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="mt-6 space-y-3" data-testid="forms-fields">
        {draft.fields.map((f, idx) => (
          <div key={idx} className="bg-white border border-brand-line rounded-2xl p-5" data-testid={`forms-field-${f.key}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex flex-col gap-1">
                <button onClick={() => moveField(idx, -1)} className="text-brand-muted hover:text-brand-dark" aria-label="Move up">▲</button>
                <button onClick={() => moveField(idx, 1)} className="text-brand-muted hover:text-brand-dark" aria-label="Move down">▼</button>
              </div>
              <GripVertical strokeWidth={1.25} className="w-4 h-4 text-brand-muted" />
              <div className="flex-1 min-w-[180px]">
                <Label className="text-[10px] uppercase tracking-widest text-brand-muted">Key (internal)</Label>
                <Input
                  value={f.key}
                  onChange={(e) => updateField(idx, { key: e.target.value })}
                  list={`keys-${draft.key}`}
                  className="h-9 mt-1 rounded-lg border-brand-line font-mono text-xs"
                />
                <datalist id={`keys-${draft.key}`}>
                  {knownKeys.map((k) => <option key={k} value={k} />)}
                </datalist>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-brand-muted">Type</Label>
                <select
                  value={f.type}
                  onChange={(e) => updateField(idx, { type: e.target.value })}
                  className="h-9 mt-1 rounded-lg border border-brand-line bg-white px-2 text-sm"
                >
                  {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-brand-dark">
                <Switch
                  checked={f.required}
                  onCheckedChange={(v) => updateField(idx, { required: v })}
                />
                Required
              </label>
              <label className="flex items-center gap-2 text-xs text-brand-dark">
                <Switch
                  checked={f.active}
                  onCheckedChange={(v) => updateField(idx, { active: v })}
                />
                Visible
              </label>
              <button
                onClick={() => removeField(idx)}
                className="ml-auto text-brand-dark hover:text-brand-primary"
                data-testid={`forms-field-delete-${f.key}`}
              >
                <Trash2 strokeWidth={1.5} className="w-4 h-4" />
              </button>
            </div>

            {/* Per-language label, placeholder, helper */}
            <div className="mt-4 space-y-3">
              {[
                { k: "label", title: "Label" },
                { k: "placeholder", title: "Placeholder" },
                { k: "helper", title: "Helper text" },
              ].map(({ k, title }) => (
                <div key={k}>
                  <Label className="text-[10px] uppercase tracking-widest text-brand-muted">{title}</Label>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
                    {SUPPORTED_LANGS.map((l) => (
                      <Input
                        key={l}
                        placeholder={l.toUpperCase()}
                        value={f[k]?.[l] || ""}
                        onChange={(e) => updateLangValue(idx, k, l, e.target.value)}
                        className="h-9 rounded-lg border-brand-line text-sm"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          onClick={addField}
          data-testid="forms-add-field"
          className="w-full bg-white border-2 border-dashed border-brand-line rounded-2xl py-5 text-sm font-semibold text-brand-dark hover:border-brand-primary hover:text-brand-primary transition-colors flex items-center justify-center gap-2"
        >
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add field
        </button>
      </div>

      <div className="mt-8 sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-full bg-brand-primary text-brand-bone h-12 px-8 shadow-lifted" data-testid="forms-save-btn-bottom">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
